// Reusable fight-poster + trading-card visual kit.
// The Arena's FightCardHero was the prototype; these primitives let every
// hero-tier card across the app share the same visual DNA (clip-path corners,
// diagonal accent stripes, ribbon, grain texture, and stat comparison).
//
// Usage — combine on any container:
//   <div style={{ clipPath: POSTER_CLIP, border: `2px solid ${color}` }} className="relative overflow-hidden">
//     <PosterStripes color={color} />
//     <PosterGrain />
//     <PosterRibbon color={color}>★ CHAMPION ★</PosterRibbon>
//     ...content
//   </div>
//
// The clip-path chips the top-right + bottom-left corners for a UFC/PPV
// poster silhouette. Corner-size defaults to 16px; heroes use 22px.

import { useRef } from "react";

export const POSTER_CLIP =
  "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))";
export const POSTER_CLIP_LG =
  "polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 22px 100%, 0 calc(100% - 22px))";
export const POSTER_CLIP_SM =
  "polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))";

/** Thick diagonal fight-poster stripes in the top-left + bottom-right corners. */
export function PosterStripes({ color, size = 128, opacity = 1 }) {
  const grad = (deg) => `linear-gradient(${deg}, ${color}dd 0 4px, transparent 6px, transparent 10px, ${color}66 12px, transparent 14px, transparent 20px, ${color}33 22px, transparent 24px)`;
  return (
    <>
      <div
        className="absolute top-0 left-0 pointer-events-none"
        style={{ width: size, height: size, background: grad("135deg"), opacity }}
      />
      <div
        className="absolute bottom-0 right-0 pointer-events-none"
        style={{ width: size, height: size, background: grad("-45deg"), opacity }}
      />
    </>
  );
}

/** Ribbon label running along the top edge (fight-poster "MAIN EVENT" strap). */
export function PosterRibbon({ color, children, size = "md" }) {
  const fontSize = size === "sm" ? 8 : 9;
  const px = size === "sm" ? 3 : 4;
  return (
    <div
      className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none z-10 flex items-center justify-center py-0.5 whitespace-nowrap"
      style={{
        background: `linear-gradient(90deg, transparent, ${color}dd, transparent)`,
        fontSize,
        paddingLeft: px * 4,
        paddingRight: px * 4,
      }}
    >
      <span
        className="font-black tracking-[0.45em] text-white/95 whitespace-nowrap"
        style={{ textShadow: `0 0 8px ${color}` }}
      >
        {children}
      </span>
    </div>
  );
}

/** Paper/grain overlay — matches the FightCardHero texture. */
export function PosterGrain({ opacity = 0.08 }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none mix-blend-overlay"
      style={{
        opacity,
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
      }}
    />
  );
}

/** One-shot section container that stamps a card with chipped corners, colored
 *  ring, stripes, grain, and a top ribbon. Use it to wrap any settings panel or
 *  content section that needs the fight-poster look. */
export function PosterSection({ color, ribbon, size = "md", className = "", children, testid, style }) {
  const clip = size === "sm" ? POSTER_CLIP_SM : size === "lg" ? POSTER_CLIP_LG : POSTER_CLIP;
  return (
    <section
      className={`relative border-2 overflow-hidden ${className}`}
      style={{
        backgroundColor: "#0A0A0A",
        borderColor: `${color}88`,
        backgroundImage: `linear-gradient(160deg, ${color}18, transparent 60%)`,
        boxShadow: `0 0 22px ${color}22, inset 0 0 22px rgba(0,0,0,0.5)`,
        clipPath: clip,
        ...(style || {}),
      }}
      data-testid={testid}
    >
      <PosterStripes color={color} size={size === "sm" ? 72 : 104} opacity={0.55} />
      <PosterGrain opacity={0.05} />
      {ribbon && (
        <PosterRibbon color={color} size={size === "sm" ? "sm" : "md"}>
          {ribbon}
        </PosterRibbon>
      )}
      <div className="relative pt-6">{children}</div>
    </section>
  );
}

/** Optional holographic mouse-follow sheen. Wraps a target; the target must
 *  set relative positioning. Returns a ref + a JSX overlay to embed. */
export function useHoloSheen() {
  const ref = useRef(null);
  const onMouseMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty("--holo-x", `${x}%`);
    el.style.setProperty("--holo-y", `${y}%`);
  };
  const sheen = (
    <div
      className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-color-dodge"
      style={{
        background:
          "radial-gradient(180px circle at var(--holo-x, 50%) var(--holo-y, 50%), rgba(255,255,255,0.14), rgba(255,200,50,0.06) 32%, transparent 55%)",
      }}
    />
  );
  return { ref, onMouseMove, sheen };
}

/**
 * UFC-style "Tale of the Tape" side-by-side stat block.
 *   rows: [{ label, c, o }, ...]        each cell is a string; larger numeric wins highlight
 *   cAccent, oAccent: colors for the two sides
 *   title:            banner text (default "TALE OF THE TAPE")
 *   compact:          smaller padding/typography (default false)
 */
export function TaleOfTheTape({ rows, cAccent = "#E13C32", oAccent = "#178AE5", title = "TALE OF THE TAPE", compact = false }) {
  if (!rows || rows.length === 0) return null;
  const rowPy = compact ? "py-1" : "py-1.5";
  const numSize = compact ? "text-xs sm:text-sm" : "text-sm sm:text-base";
  return (
    <div
      className="relative rounded-sm overflow-hidden"
      style={{
        border: "1px solid rgba(255,255,255,0.08)",
        background: "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.35))",
      }}
      data-testid="tale-of-the-tape"
    >
      <div className="text-center py-1 border-b border-white/5 bg-white/[0.02]">
        <span className="font-black text-[9px] tracking-[0.4em] text-white/80">{title}</span>
      </div>
      <div>
        {rows.map((row, i) => {
          // Compare numerically when possible — otherwise lexicographically.
          const cNum = parseFloat(String(row.c).replace(/[^\d.-]/g, ""));
          const oNum = parseFloat(String(row.o).replace(/[^\d.-]/g, ""));
          const cWins = !Number.isNaN(cNum) && !Number.isNaN(oNum) ? cNum > oNum : String(row.c) > String(row.o);
          const oWins = !Number.isNaN(cNum) && !Number.isNaN(oNum) ? oNum > cNum : String(row.o) > String(row.c);
          return (
            <div
              key={row.label + i}
              className={`grid grid-cols-[1fr_auto_1fr] items-center px-3 ${rowPy}`}
              style={{ borderTop: i > 0 ? "1px dashed rgba(255,255,255,0.06)" : "none" }}
            >
              <div
                className={`font-mono font-black leading-none text-left ${numSize}`}
                style={{ color: cWins ? cAccent : "rgba(255,255,255,0.7)" }}
              >
                {row.c}
              </div>
              <div className="text-center px-3">
                <span className="text-[9px] font-black tracking-[0.28em] text-white/45">{row.label}</span>
              </div>
              <div
                className={`font-mono font-black leading-none text-right ${numSize}`}
                style={{ color: oWins ? oAccent : "rgba(255,255,255,0.7)" }}
              >
                {row.o}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
